import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill } from "../../components/analytics-kit";
import { Loader, ChartLoader } from "../../components/loaders";
import Error from "../../components/error";
import Chart from "../../components/chart";
import { hasTenantFeature } from "../../functions/tenant-features";
import { RANGES, usd, num, aov, trendLabel, rangeLabelFor, resolveBusinessModel } from "../sales/territory-common";
import { getGlobalRange, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { decodeEntities, categoryLeaf, pct } from "../stores/common";
import { getDealerDetail, getDealerPerformance, dealerRows, dealerTotals, lapseKey, lapseLabel, lapseTone } from "../sales/sales-api";
import { canViewQuotes } from "../sales/sales-quotes-api";

/*
 * Dealer detail (SALES_FEATURES_REVIEW N1; the Stores rail's store detail
 * relabelled): one read: GET /v1/sales/dealers/{range}/{dealerId} → KPIs +
 * daily trend + topCategories + customer mix. Plan/pace context comes from
 * the roster read (targets are not on the detail payload), fetched alongside.
 * The Territory dealer drill (orders by product, rep, trend vs prior) stays
 * one click away.
 */
class DealerDetailView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: undefined,
      rosterRow: null,
      hasTargets: false,
      error: false,
      businessModel: null
    };
    this._alive = true;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange();
  }
  dealerId() {
    return decodeURIComponent(this.props.params && this.props.params.dealerId || "");
  }
  componentDidMount() {
    resolveBusinessModel().then(bm => this._alive && this.setState({
      businessModel: bm
    })).catch(() => this._alive && this.setState({
      businessModel: "both"
    }));
    this.load();
    this._periodTok = subscribePeriod(({
      range
    }) => {
      if (!this._alive || !range) return;
      const target = `/Dealers/${range}/dealer/${encodeURIComponent(this.dealerId())}`;
      if (window.location.pathname === target) {
        this.setState({
          data: undefined,
          rosterRow: null,
          error: false
        }, () => this.load());
      } else {
        this.props.history.push(target);
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const p = prev.params || {};
    const c = this.props.params || {};
    if (p.range !== c.range || p.dealerId !== c.dealerId) {
      this.setState({
        data: undefined,
        rosterRow: null,
        error: false
      });
      this.load();
    }
  }
  load() {
    const range = this.range();
    const id = this.dealerId();
    getDealerDetail(range, id).then(d => this._alive && this.setState({
      data: d === null ? null : d || {}
    })).catch(() => this._alive && this.setState({
      error: true
    }));
    getDealerPerformance(range).then(r => {
      if (!this._alive) return;
      const row = dealerRows(r).find(s => String(s.dealerId) === String(id)) || null;
      this.setState({
        rosterRow: row,
        hasTargets: dealerTotals(r).hasTargets
      });
    }).catch(() => {});
  }

  // Daily rows are noisy over long windows: bucket to weeks past ~70 points.
  chartData(trend) {
    if (!trend || trend.length < 2) return null;
    let rows = trend.map(t => ({
      date: String(t.date || t.period).slice(0, 10),
      revenue: Number(t.revenue != null ? t.revenue : t.totalAmount) || 0,
      orders: Number(t.orders) || 0
    }));
    if (rows.length > 70) {
      const byWeek = new Map();
      rows.forEach(r => {
        const d = new Date(r.date + "T00:00:00");
        const monday = new Date(d);
        monday.setDate(d.getDate() - (d.getDay() + 6) % 7);
        const key = monday.toISOString().slice(0, 10);
        const cur = byWeek.get(key) || {
          date: key,
          revenue: 0,
          orders: 0
        };
        cur.revenue += r.revenue;
        cur.orders += r.orders;
        byWeek.set(key, cur);
      });
      rows = [...byWeek.values()].sort((a, b) => a.date < b.date ? -1 : 1);
    }
    const data = [["Period", "Revenue", "Orders"]];
    rows.forEach(r => data.push([trendLabel(r.date, "day"), Math.round(r.revenue * 100) / 100, r.orders]));
    return data;
  }
  render() {
    if (!hasTenantFeature("territoryRep") || this.state.businessModel === "retail") {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Dealer performance is not configured for this workspace."));
    }
    const {
      data,
      rosterRow,
      hasTargets,
      error
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (data === undefined) return /*#__PURE__*/React.createElement(Loader, null);
    const range = this.range();
    const id = this.dealerId();
    const name = data && data.name || rosterRow && rosterRow.name || id;
    if (data === null) {
      return /*#__PURE__*/React.createElement("div", {
        className: "territory-view territory-detail stores-view wa-view"
      }, /*#__PURE__*/React.createElement(PageHeader, {
        crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
          to: `/Dealers/${range}`
        }, "Dealers"), " \xB7 Dealer"),
        title: name,
        subtitle: rangeLabelFor(range, RANGES)
      }), /*#__PURE__*/React.createElement("div", {
        className: "wa-grid"
      }, /*#__PURE__*/React.createElement(Card, {
        title: "Dealer performance"
      }, /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, "Dealer detail is not available yet for this workspace. The ", /*#__PURE__*/React.createElement("a", {
        style: {
          cursor: "pointer"
        },
        onClick: () => this.props.history.push(`/Territory/${range}/customer/${encodeURIComponent(id)}`)
      }, "Territory dealer drill"), " has this account's orders, products and trend."))));
    }
    const chart = this.chartData(data.trend);
    const cats = (data.topCategories || []).slice(0, 12);
    const newPct = Number(data.distinctCustomers) > 0 ? Math.round(Number(data.newCustomers) / Number(data.distinctCustomers) * 100) : null;
    const lk = lapseKey(data.lapseRisk || rosterRow && rosterRow.lapseRisk);
    const rep = data.assignedRepName || data.assignedRepId || rosterRow && (rosterRow.assignedRepName || rosterRow.assignedRepId);
    const loc = [data.city || rosterRow && rosterRow.city, data.region || rosterRow && rosterRow.region].filter(Boolean).join(", ");
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view territory-detail stores-view dealers-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: `/Dealers/${range}`
      }, "Dealers"), " \xB7 Dealer"),
      title: name,
      subtitle: [`#${id}`, loc || null, rep ? `assigned to ${rep}` : null, rangeLabelFor(range, RANGES), rosterRow && hasTargets ? `plan ${usd(rosterRow.targetRevenue)} · attainment ${pct(rosterRow.attainmentPct)} · pace to end ${pct(rosterRow.paceAttainmentPct)}` : null, rosterRow ? `${pct(rosterRow.sharePct)} of wholesale revenue` : null].filter(Boolean).join(" · "),
      chips: [lk ? {
        k: "Health",
        v: /*#__PURE__*/React.createElement(Pill, {
          tone: lapseTone(lk)
        }, lapseLabel(lk))
      } : null],
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.props.history.push(`/Territory/${range}/customer/${encodeURIComponent(id)}`)
      }, "Orders and products"), hasTenantFeature("quotes") && canViewQuotes() ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.props.history.push(`/Quotes?customerId=${encodeURIComponent(id)}&customer=${encodeURIComponent(name)}`)
      }, "Quotes"), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
        onClick: () => this.props.history.push(`/Quotes/new?customerId=${encodeURIComponent(id)}`)
      }, "New quote")) : null)
    }), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      tiles: [{
        l: "Revenue",
        v: usd(data.revenue != null ? data.revenue : data.totalAmount)
      }, {
        l: "Orders",
        v: num(data.orders)
      }, {
        l: "Units",
        v: num(data.units != null ? data.units : data.quantity)
      }, {
        l: "Average order value",
        v: aov(data.revenue != null ? data.revenue : data.totalAmount, data.orders)
      }, {
        l: "Ship-to locations",
        v: num(data.distinctCustomers),
        s: data.distinctCustomers == null ? "not on this feed" : null
      }, {
        l: "New this range",
        v: num(data.newCustomers),
        s: newPct != null ? `${newPct}% of active` : null
      }]
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, chart && /*#__PURE__*/React.createElement(Card, {
      title: "Sales trend",
      hint: "revenue bars \xB7 orders line"
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-trend"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "320px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: chart,
      options: {
        seriesType: "bars",
        series: {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358",
            tooltipFormat: "currency"
          },
          1: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237",
            tooltipFormat: "{0:n0}"
          }
        },
        vAxes: {
          0: {
            title: "Revenue",
            format: "short"
          },
          1: {
            title: "Orders",
            format: "0"
          }
        },
        legend: {
          position: "top"
        }
      }
    }))), cats.length > 0 && /*#__PURE__*/React.createElement(Card, {
      title: "Top categories",
      hint: `at ${name} · top 12 by revenue`
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Category"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Revenue"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Units"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "% of dealer"))), /*#__PURE__*/React.createElement("tbody", null, cats.map(c => /*#__PURE__*/React.createElement("tr", {
      key: c.category
    }, /*#__PURE__*/React.createElement("td", {
      title: decodeEntities(c.category)
    }, categoryLeaf(c.category)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, usd(c.revenue)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, num(c.units)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, pct(c.sharePct))))))), cats.some(c => c.category === "(uncategorized)") && /*#__PURE__*/React.createElement("p", {
      className: "wa-foot"
    }, "Uncategorized rows are sales whose product feed rows carry no product type: a feed coverage gap, shown rather than hidden."))));
  }
}
export default withRouter(DealerDetailView);